import createDebug from "debug";
import { Deserializer } from "jsonapi-serializer";
import { ROLES } from "../constants/preset-constants";
import { INTERNAL_SERVER_ERROR, NO_CONTENT, OK } from "../constants/response-status-codes";
import { BuildDetail } from "../models/BuildDetails";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { UserLogin, UserLoginWrapper } from "../models/UserLogin";
import { getTodayDate } from "../utilities/DateUtils";
import { convertTextPwdToBase64Pwd } from "../utilities/Validation";
import { APIConnector } from "./api/APIConnector";
import { getBuildDetails } from "./systemService";
import { adminUserPermissions } from "./userPermissions/administratorPermissions";
import { installerUserPermissions } from "./userPermissions/installerUserPermissions";
import { reportUserPermissions } from "./userPermissions/reportUserPermissions";
import { siteGuardUserPermissions } from "./userPermissions/siteGuardUserPermissions";
import { siteReceptionistPermissions } from "./userPermissions/siteReceptionistPermissions";
import { systemOwnerPermissions } from "./userPermissions/systemOwnerPermissions";
import { createPagination } from "./utilities/paginationBuilder";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import {isAuthLogin, isAuthWithGroupsLogin, isLdapLogin} from "./utilities/loginsUtils";
import { getUserAdvancePermissions } from "./advancePermissionService";
import { AdvancePermission } from "@/models/AdvancePermission";

interface LoginResponse {
    token: string;
    role: string;
    userId: number;
}

interface LoginRequest {
    username: string;
    password: string;
}

interface LoginData {
  data: {
    type: string;
    id: string;
    relationships: {
      profile: {
        data: {
          type: string;
          id: string;
        };
      };
    };
  };
}

const debug = createDebug("login-service");
const apiConnector = new APIConnector();
const EXCLUDE_LOGINS_BEFORE_ID = 49; // Do not show any logins with an id of 49 or below.

const licensedMenus = [
    { name: "Operational Compliance", route: "operational-compliance", id: "22" },
];

const filterPermissions = (permissionsObject, licensedIds) => {
    let itemsToAdd = [] as any;
    let cleanLicenseItems = [] as any;
    const noLicenseItems = permissionsObject.filter(
        (item) => !item.checkLicense
    );
    const licenseItems = permissionsObject.filter((item) => item.checkLicense);

    licensedMenus.map((item) => {
        licensedIds.map((id) => {
            if (id === item.id) {
                itemsToAdd.push(item);
            }
        });
    });

    licenseItems.map((license) => {
        itemsToAdd.map((item) => {
            if (item.route === license.route) {
                cleanLicenseItems.push(license);
            }
        });
    });

    return [...noLicenseItems, ...cleanLicenseItems];
};

const generateStringPermissions = async (permissionsObject) => {
    let permissions: string[] = [];
    const response: {success: boolean, buildDetail: BuildDetail} = await getBuildDetails();
    const licensedIds: string[] = response.buildDetail.licenceIds ?? []

    const filteredPermissions = filterPermissions(
        permissionsObject,
        licensedIds
    );

    await filteredPermissions.map((permission) => {
        if (permission.addElements)
            permissions.push(`add.${permission.route}.element`);
        if (permission.showListItem)
            permissions.push(`show.${permission.route}.element`);
        if (permission.deleteElements)
            permissions.push(`delete.${permission.route}.element`);
        if (permission.updateElements)
            permissions.push(`update.${permission.route}.element`);
    });
    return permissions;
};

const buildPermissions = async (profileId, inheritsFrom) => {
    let defaultView = "enrolment";
    let permissionsToUse: UserPermission[] = [];

    // System Owner
    if (isRole(ROLES.ID_SYSTEM_OWNER, profileId, inheritsFrom)) {
        permissionsToUse = systemOwnerPermissions;
    }
    // Site Admin
    else if (isRole(ROLES.ID_SITE_ADMIN, profileId, inheritsFrom)) {
        permissionsToUse = adminUserPermissions;
    }
    // Installer
    else if (isRole(ROLES.ID_INSTALLER, profileId, inheritsFrom)) {
        defaultView = "reports";
        permissionsToUse = installerUserPermissions;
    }
    // Reports
    else if (isRole(ROLES.ID_REPORTS, profileId, inheritsFrom)) {
        defaultView = "reports";
        permissionsToUse = reportUserPermissions;
    }
    // Receptionist
    else if (isRole(ROLES.ID_RECEPTION, profileId, inheritsFrom)) {
        permissionsToUse = siteReceptionistPermissions;
    }
    // Guard
    else if (isRole(ROLES.ID_GUARD, profileId, inheritsFrom)) {
        defaultView = "reports";
        permissionsToUse = siteGuardUserPermissions;
    }

    if (permissionsToUse) {
        localStorage.setItem("indexRoute", defaultView); // Default to this view.
        return await generateStringPermissions(permissionsToUse);
    }
}

const isRole = (role, profileId, inheritsFrom) => {
    return profileId === role || inheritsFrom === role;
};

export const storeSecurityPolicy = async (userLoginId: String, securityPolicyId: String, authLogin) => {
    try {
        const newRecord = {
            "data": {
                "type": "securityPolicy",
                "id": 1,
                "attributes": {
                    "userLoginId": userLoginId,
                    "securityPolicyId": securityPolicyId,
                    "encodedPassword": convertTextPwdToBase64Pwd(authLogin.password),
                    "sevenDaysTillPasswordHasExpired":false,
                    "passwordExpired": false,
                    "oneDayTillPasswordHasExpired":false,
                    "firstLogin": false,
                    "lessThanOneMonthTillPasswordExpired": false,
                    "passwordIsGood": false,
                    "failsRequirementCheck":false,
                    "isUsingDefaultPassword": false
                }
            }
        };
        const url = `securityPolicy`;
        let response;

        await apiConnector
            .sendData(url, true, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    reject(processServerError(error));
                });
            });

        return new Promise(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({
                        status: response.status,
                        message: content.errors[0].detail,
                    });
                }

                new Deserializer().deserialize(content, async (error, securityResponse) => {
                    return resolve({
                        userLoginId: securityResponse["user-login-id"],
                        sevenDaysToExpire: securityResponse["seven-days-till-password-has-expired"],
                        passwordExpired: securityResponse["password-expired"],
                        oneDayToExpire: securityResponse["one-day-till-password-has-expired"],
                        oneMonthToExpire: securityResponse["less-than-one-month-till-password-expired"],
                        passwordIsGood: securityResponse["password-is-good"],
                        firstLogin: securityResponse["first-login"],
                        failsRequirementCheck: securityResponse["fails-requirement-check"],
                        isUsingDefaultPassword: securityResponse["is-using-default-password"]
                    });
                });
                return;
            }

            return reject({ status: response.status, success: false, message: genericError });
        });
    }
    catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const authLogin = async (authLogin, headers, createLogin = true) => {
    try {
        const newRecord = {
            data: {
                type: "authLogin",
                id: "1",
                attributes: {
                    username: authLogin?.username ? authLogin.username : '',
                    password: authLogin.password ? authLogin.password : '',
                    ...(authLogin.authCode !== undefined && authLogin.authCode !== null
                        ? { validationCode: authLogin.authCode }
                        : {})
                },
            },
        };

        const url = `authLogin`;
        let response;

        await apiConnector
            .sendDataWithHeaders(url, createLogin, newRecord, headers)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    reject(processServerError(error));
                });
            });

        return new Promise(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({
                        status: response.status,
                        message: getConstraintViolation(content.errors[0].detail),
                    });
                }

                new Deserializer().deserialize(
                    content,
                    async (error, newLogin) => {
                        const accessToken = newLogin.token?.["access-token"] ?? "";
                        return resolve({
                            userLoginId: newLogin["user-login-id"],
                            userName: newLogin["username"],
                            accessToken: accessToken,
                            status: newLogin["status"],
                            qrCode: newLogin["qr-code"],
                        });
                    }
                );
                return;
            }

            return reject({
                status: response.status,
                success: false,
                message: genericError,
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const authLogout = async (authLogout, createLogout = true) => {
    try {
        const newRecord = {
            data: {
                type: "authLogout",
                id: "1",
                attributes: {
                    userLoginId: authLogout.id,
                    reason: "user disconnected",
                },
            },
        };

        const url = `authLogout`;
        let response;

        await apiConnector
            .sendData(url, createLogout, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    reject(processServerError(error));
                });
            });

        return new Promise(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({
                        status: response.status,
                        message: getConstraintViolation(content.errors[0].detail),
                    });
                }

                new Deserializer().deserialize(content, (error, newLogout) => {
                    if (error) {
                        return reject({
                            status: response.status,
                            id: newLogout.id,
                            success: false,
                        });
                    }

                    return resolve({ id: newLogout.id, success: true });
                });

                return;
            }

            return reject({
                status: response.status,
                success: false,
                message: genericError,
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getUserLoginsBySecurityPolicyId = async (listParameters?: ListQuery): Promise<UserLoginWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter === "" ? `&filter=id>${EXCLUDE_LOGINS_BEFORE_ID}` : `${filter};id>${EXCLUDE_LOGINS_BEFORE_ID}`;
        const includes = "include=master,profile,site";
        let userLoginFields = "fields[userLogin]=ulUsername,ulCommonName,master,siteFilter,";
        userLoginFields += "userLoginTypeId,site,profile,isDefault";
        const masterFields = "fields[master]=mstDisplayname,mstIdnumber";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        let url = `userLogin?${includes}&${userLoginFields}&${masterFields}&`;
        url += `${siteFields}&${profileFields}`;
        url += createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const userLogins: UserLogin[] = data.map((userLogin): UserLogin => {
                        return {
                            id: userLogin["id"],
                            name: userLogin["ul-username"],
                            ulCommonName: userLogin["ul-common-name"],
                            displayName: userLogin.master == null ? null : userLogin.master["mst-displayname"],
                            idNumber: userLogin.master == null ? null : userLogin.master["mst-idnumber"],
                            siteFilter: userLogin["site-filter"],
                            loginTypeId: userLogin["user-login-type-id"],
                            origLoginTypeId: userLogin["user-login-type-id"],
                            isChangingLoginTypes: userLogin["is-changing-login-types"],
                            master: !userLogin.master ? "" : userLogin.master["mst-displayname"],
                            masterId: !userLogin.master ? 0 : userLogin.master["id"],
                            site: userLogin["site"]["site-name"],
                            siteId: userLogin["site"]["id"],
                            profile: userLogin.profile == null ? null : userLogin.profile["trn-name"],
                            profileId: userLogin.profile == null ? null : userLogin.profile["id"],
                            securityPolicy: userLogin["security-policy"] == null ? null : userLogin["security-policy"]["trn-name"],
                            securityPolicyId: userLogin["security-policy"] == null ? null : userLogin["security-policy"]["id"],
                            isDefault: userLogin["is-default"],
                        };
                    }
                );

                return resolve({ rows: userLogins, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getUserLogins = async (listParameters?: ListQuery, loggedInUserProfileId?: number): Promise<UserLoginWrapper> => {
    try {
        const userLoginFields = '&fields[userLogin]=ulUsername,ulCommonName,master,siteFilter,userLoginTypeId,site,profile,securityPolicy,isDefault,userLoginTypeName';
        const masterFields = '&fields[master]=mstDisplayname,mstIdnumber';
        const siteFields = '&fields[site]=siteName';
        const profileFields = '&fields[profile]=trnName';
        const securityPolicyFields = '&fields[securityPolicy]=trnName';
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter === "" ? `&filter=id>${EXCLUDE_LOGINS_BEFORE_ID}` : `${filter};id>${EXCLUDE_LOGINS_BEFORE_ID}`;
        const url = "userLogin?include=master,profile,site,securityPolicy" + createPartialURL( listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + userLoginFields + masterFields + siteFields + profileFields + securityPolicyFields;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const userLogins: UserLogin[] = data.map(
                    (userLogin): UserLogin => {
                        return {
                            id: userLogin["id"],
                            name: userLogin["ul-username"],
                            ulCommonName: userLogin["ul-common-name"],
                            displayName: userLogin.master == null ? null : userLogin.master["mst-displayname"],
                            idNumber: userLogin.master == null ? null : userLogin.master["mst-idnumber"],
                            siteFilter: userLogin["site-filter"],
                            loginTypeId: userLogin["user-login-type-id"],
                            origLoginTypeId: userLogin["user-login-type-id"],
                            isChangingLoginTypes: userLogin["is-changing-login-types"],
                            master: !userLogin.master ? "" : userLogin.master["mst-displayname"],
                            masterId: !userLogin.master ? 0 : userLogin.master["id"],
                            site: userLogin["site"]["site-name"],
                            siteId: userLogin["site"]["id"],
                            profile: userLogin.profile == null ? null : userLogin.profile["trn-name"],
                            profileId: userLogin.profile == null ? null : userLogin.profile["id"],
                            securityPolicy: userLogin["security-policy"] == null ? null : userLogin["security-policy"]["trn-name"],
                            securityPolicyId: userLogin["security-policy"] == null ? null : userLogin["security-policy"]["id"],
                            isDefault: userLogin["is-default"],
                            userLoginTypeName: userLogin["user-login-type-name"],
                        };
                    }
                );

                if (loggedInUserProfileId !== ROLES.ID_SYSTEM_OWNER) {
                    const newUserLogins = userLogins.filter(userLogin => Number(userLogin.profileId) !== ROLES.ID_SYSTEM_OWNER);   //Todo - Investigate type casting
                    pagination.totalCount = newUserLogins.length;
                    return resolve({ rows: newUserLogins, pagination });
                }

                return resolve({ rows: userLogins, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getLoginById = async (loginId: string) => {
    try {
        const userLoginFields = "fields[userLogin]=ulUsername,ulCommonName,ulNewPassword,siteFilter,userLoginTypeId,isDefault,master,site,profile,securityPolicy,isPasswordCaptured,ulMfaEnabled,userLoginOid,userLoginEmail";
        const masterFields = "fields[master]=mstDisplayname,mstIdnumber,mstLastname,mstFirstname,masterImages";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const securityPolicyFields = "fields[securityPolicy]=trnName"
        const masterImageFields = "fields[masterImage]=base64Thumb";
        const include = "include=master,site,profile,master.masterImages,securityPolicy";
        const url = `userLogin/${loginId}?${userLoginFields}&${masterFields}&${siteFields}&${profileFields}&${securityPolicyFields}&${include}&${masterImageFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, async (error: any, response: any) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError, });
                    }

                    const hasMaster = response.master !== null;
                    const inheritsFrom = json?.included[0].relationships?.inheritsFrom?.data?.id;
                    const login = {
                        id: response["id"],
                        name: response["ul-username"],
                        ulCommonName: response["ul-common-name"],
                        password: response["ul-new-password"],
                        displayName: hasMaster ? response["master"]["mst-displayname"] : undefined,
                        idNumber: hasMaster ? response["master"]["mst-idnumber"] : undefined,
                        siteFilter: response["site-filter"],
                        loginTypeId: response["user-login-type-id"],
                        origLoginTypeId: response["user-login-type-id"],
                        isChangingLoginTypes: response["is-changing-login-types"],
                        master: hasMaster ? response["master"]["mst-displayname"] : undefined,
                        masterId: hasMaster ? response["master"]["id"] : undefined,
                        masterImage: hasMaster && response["master"]["master-images"].length > 0 ? response["master"]["master-images"][0][ "base64-thumb"] : "",
                        site: response["site"]["site-name"],
                        siteId: response["site"]["id"],
                        profile: response["profile"] == null ? null : response["profile"]["trn-name"],
                        profileId: response["profile"] == null ? null : parseInt(response["profile"]["id"]),
                        securityPolicy: response["security-policy"] == null ? null : response["security-policy"]["trn-name"],
                        securityPolicyId: response["security-policy"] == null ? null : parseInt(response["security-policy"]["id"]),
                        lastName: hasMaster ? response["master"]["mst-lastname"] : undefined,
                        firstName: hasMaster ? response["master"]["mst-firstname"] : undefined,
                        permissions: await buildPermissions(response["profile"]["id"] * 1, inheritsFrom * 1),
                        isDefault: response["is-default"],
                        isPasswordCaptured: response["is-password-captured"],
                        uniqueIdentifier: response["ul-username"], // For LDAP logins this is the uid
                        mfaEnabled: response["ul-mfa-enabled"] == 1,
                        userOid: response["user-login-oid"] ? response["user-login-oid"] : '', // Entra Oid for user login
                        userEmail: response["user-login-email"]
                    };
                    return resolve(login);
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getLoginNamesWithPagination = async (listQuery?: ListQuery, loggedInUserProfileId?: number, bypassChecks?: boolean): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause( listQuery?.filters, listQuery?.searchTerm);
        const baseLoginFilter = bypassChecks === true ? `(id==1,id>${EXCLUDE_LOGINS_BEFORE_ID})` : `id>${EXCLUDE_LOGINS_BEFORE_ID}`;
        const paginationFilters = filters.length > 0 ? filters.replace("filter=", `filter=${baseLoginFilter};`) : `&filter=${baseLoginFilter}`;
        const paginationString = createPartialURL( listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, paginationFilters);
        const url = `userLogin?include=profile&fields[userLogin]=ulUsername,isDefault,profile${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const logins: Name[] = [];

                data.forEach((login) => {
                    if (!!bypassChecks && loggedInUserProfileId !== ROLES.ID_SYSTEM_OWNER && parseInt(login["profile"]["id"]) === ROLES.ID_SYSTEM_OWNER) {
                        return;
                    }

                    logins.push({
                        value: login["id"],
                        label: login["ul-username"],
                        isDefault: login["is-default"],
                    });
                });

                newPagination.totalCount = logins.length;
                return resolve({ rows: logins, pagination: newPagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const storeLogin = async (userLogin, createLogin = true) => {
    try {
        let attributes: any = {
            siteFilter: userLogin.siteFilter,
            ulLicense: userLogin.ulLicense,
            ulReset: userLogin.ulReset,
            creationDate: getTodayDate(),
            ulUsername: userLogin.username,
            userLoginTypeId: userLogin.loginTypeId ?? 0,
            ulMfaEnabled: userLogin.mfaEnabled ? 1 : 0
        };
        const isLdap = isLdapLogin(userLogin.loginTypeId);
        const isAuth = isAuthLogin(userLogin.loginTypeId);
        const isAuthWithGroups = isAuthWithGroupsLogin(userLogin.loginTypeId);
        if (!isLdap && !isAuth && !isAuthWithGroups) { // Add attributes needed for saving a Manual Login
            attributes = {
                ...attributes,
                ulNewPassword: userLogin.ulPassword && convertTextPwdToBase64Pwd(userLogin.ulPassword),
                ulConfirmPassword: userLogin.ulConfirmPassword && convertTextPwdToBase64Pwd(userLogin.ulConfirmPassword),
                isChangingLoginTypes: userLogin.isChangingLoginTypes ?? false,
            }
        }
        else if (isLdap || isAuth || isAuthWithGroups) { // Add attributes needed for saving a LDAP Login
            attributes = {
                ...attributes,
                ulCommonName: userLogin.ulCommonName,
                isChangingLoginTypes: userLogin.isChangingLoginTypes ?? false,
            }
            if (isAuth || isAuthWithGroups) { // Add attributes specific for saving an Entra Login
                attributes = {
                    ...attributes,
                    userLoginOid: userLogin.userLoginOid,
                    userLoginEmail: userLogin.userLoginEmail
                }
            }
        }

        const newRecord = {
            data: {
                type: "userLogin",
                id: userLogin.id,
                attributes: attributes,
                relationships: {
                    profile: {
                        data: {
                            type: "profile",
                            id: userLogin.profileId,
                        },
                    },
                    site: {
                        data: {
                            type: "site",
                            id: userLogin.siteId,
                        },
                    },
                    ...((!isLdap && !isAuth && !isAuthWithGroups) && {
                        securityPolicy: {
                            data: {
                                type: "profile",
                                id: userLogin.securityPolicyId,
                            }
                        }
                    }),
                    ...(userLogin.masterId && {
                        master: {
                            data: {
                                type: "master",
                                id: userLogin.masterId,
                            },
                        },
                    }),
                },
            },
        };

        const url = `userLogin${createLogin ? "" : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector
            .sendData(url, createLogin, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    reject(processServerError(error));
                });
            });

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (createLogin) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({
                            status: response.status,
                            message: getConstraintViolation(content.errors[0].detail),
                        });
                    }

                    new Deserializer().deserialize(
                        content,
                        (error, newLogin) => {
                            if (error) {
                                return reject({
                                    status: response.status,
                                    id: newLogin.id,
                                    success: false,
                                    message: genericError,
                                });
                            }
                            return resolve({ id: newLogin.id, success: true });
                        }
                    );
                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: userLogin.id, success: true });
                }

                const content = await response.json();
                return reject({
                    status: response.status,
                    id: userLogin.id,
                    success: false,
                    message: getConstraintViolation(content.errors[0].detail),
                });
            }

            return reject({
                status: INTERNAL_SERVER_ERROR,
                id: userLogin.id,
                success: false,
                message: genericError,
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteUserLogin = async (
    id: string
): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `userLogin/${id}`;
        const userLogin = {
            data: {
                type: "userLogin",
                id: id,
            },
        };

        const response = await apiConnector.deleteData(url, userLogin);
        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({
                status: response.status,
                id: id,
                success: false,
                message: getConstraintViolation(json.errors[0].detail),
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

/**
 * Transforms the login ID and permissions ID into the required data format
 * @param permissionsIdstring - The permissions ID
 * @param loginId - The login ID
 * @returns The transformed login data
 */
const buildLoginData = (permissionsIdstring: string, loginId: string): LoginData => {
  return {
    data: {
      type: "userLogin",
      id: loginId,
      relationships: {
        profile: {
          data: {
            type: "profile",
            id: permissionsIdstring,
          },
        },
      },
    },
  };
};

/**
 * Assigns or unassigns permissions to/from a list of logins
 * @param permissionsIdstring - The permissions ID
 * @param loginIdListToAdd - The array of login IDs to which the permissions should be assigned
 * @returns A promise that resolves to { success: true } if all endpoints succeed, otherwise rejects with { success: false }
 */
export const assignUnassignPermissions = async (
    permissionsIdstring: string,
    loginIdListToAdd: string[]
): Promise<{
    status: number;
    message?: string;
}> => {
    try {

        const loginsToAdd = loginIdListToAdd.map((loginId) =>
            buildLoginData(permissionsIdstring, loginId)
        );

         const addedLogins = await Promise.all(
             loginsToAdd.map(async (loginData: LoginData) => {
                 return await apiConnector.sendData(
                     `userLogin/${loginData.data.id}`,
                     false,
                     loginData
                 );
             })
         );

        const hasFailed = addedLogins.some((element) => element?.ok === false);
        if (hasFailed) {
            return Promise.reject(addedLogins);
        }
        return Promise.resolve({ message: 'request successful', success: true, status: OK });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const changeUserPassword = async (userId: string, newPassword: string, oldPassword: string) => {
    try {
        const record = {
            data: {
                type: "userLogin",
                id: userId,
                attributes: {
                    ulNewPassword: convertTextPwdToBase64Pwd(newPassword),
                    ulConfirmPassword: convertTextPwdToBase64Pwd(oldPassword),
                    ulReset: 1,
                }
            }
        }
        let response: Response;

        await apiConnector.sendData(`userLogin/${userId}`, false, record).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.status === NO_CONTENT) {
                    return resolve({ id: userId, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: userId, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: userId, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
